/**
 * Export replacement for `tabulator.download(...)`.
 *
 * Uses deps already in package.json: xlsx, jspdf, jspdf-autotable.
 *
 * IMPORTANT — preserved semantics: under Tabulator's remote pagination only the
 * CURRENT PAGE was ever in memory, so `download(..., "all")` exported the current
 * page, not the full result set. That is kept. Do not silently "upgrade" it to
 * fetch every page: on the application listing that turns a 10-row export into
 * thousands of rows and a burst of sequential API calls per click.
 * Full-dataset export already exists separately in BreadTabulatorExportAll.vue.
 */

import { getFieldValue } from "./fieldValue";

function cellValue(row, header) {
  const def = header._def || {};
  const field = header.field;
  const raw = getFieldValue(row, field);
  if (typeof def.accessorDownload === "function") {
    try {
      return def.accessorDownload(raw, row);
    } catch (e) {
      /* fall through to raw */
    }
  }
  return raw === null || raw === undefined ? "" : raw;
}

function exportableHeaders(headers) {
  return headers.filter((h) => {
    const def = h._def || {};
    return def.download !== false && h.field;
  });
}

function toMatrix(rows, headers) {
  const cols = exportableHeaders(headers);
  const head = cols.map((h) => h.text);
  const body = rows.map((row) =>
    cols.map((h) => {
      const v = cellValue(row, h);
      return v === null || v === undefined ? "" : v;
    })
  );
  return { head, body };
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function toCsv(rows, headers, filename) {
  const { head, body } = toMatrix(rows, headers);
  const esc = (v) => {
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [head.map(esc).join(",")].concat(
    body.map((r) => r.map(esc).join(","))
  );
  // BOM so Excel reads UTF-8 correctly.
  const blob = new Blob(["﻿" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });
  downloadBlob(blob, filename);
}

function toXlsx(rows, headers, filename, sheetName) {
  const XLSX = require("xlsx");
  const { head, body } = toMatrix(rows, headers);
  const sheet = XLSX.utils.aoa_to_sheet([head].concat(body));
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, (sheetName || "Sheet1").slice(0, 31));
  XLSX.writeFile(book, filename);
}

function toPdf(rows, headers, filename, title) {
  const { jsPDF } = require("jspdf");
  require("jspdf-autotable");
  const { head, body } = toMatrix(rows, headers);
  const doc = new jsPDF({ orientation: "landscape" });
  if (title) doc.text(title, 14, 14);
  doc.autoTable({ head: [head], body, startY: title ? 20 : 14, styles: { fontSize: 7 } });
  doc.save(filename);
}

/**
 * @param {string} type      'csv' | 'xlsx' | 'pdf'
 * @param {Array}  rows      rows to export (current page, or the selection)
 * @param {Array}  headers   adapted headers, in display order
 * @param {object} opts      { filename, sheetName, title }
 */
export function exportRows(type, rows, headers, opts) {
  const o = opts || {};
  const name = o.filename || `export.${type}`;
  if (type === "csv") return toCsv(rows, headers, name);
  if (type === "xlsx") return toXlsx(rows, headers, name, o.sheetName);
  if (type === "pdf") return toPdf(rows, headers, name, o.title);
  // eslint-disable-next-line no-console
  console.warn(`[BreadDataTable] unknown export type "${type}"`);
}
